import { useCallback, useEffect, useMemo } from 'react';

import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { calculatePos } from '@trello/arrays';
import { useMemberId } from '@trello/authentication';
import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { calculateItemPosition } from '@trello/position';
import { useSharedStateSelector } from '@trello/shared-state';

import {
  isDragging,
  onDropStarredBoard,
  starredBoardDragAndDropState,
} from './starredBoardDragAndDropState';
import { starredBoardsState } from './starredBoardsState';
import { useUpdateStarredBoardMutation } from './UpdateStarredBoardMutation.generated';

type DraggableStarredBoardData = {
  type: 'trello/starred-board';
  boardId: string;
};

export const isDraggableStarredBoard = (
  data: Record<string | symbol, unknown>,
): data is DraggableStarredBoardData =>
  'type' in data && data.type === 'trello/starred-board';

interface StarredBoardDragAndDropOptions {
  ref?: React.RefObject<HTMLDivElement>;
  selectedBoardId: string;
}

export const useStarredBoardDragAndDrop = ({
  ref,
  selectedBoardId,
}: StarredBoardDragAndDropOptions) => {
  const memberId = useMemberId();
  const [updateStarredBoard] = useUpdateStarredBoardMutation();
  const starredBoards = useSharedStateSelector(
    starredBoardsState,
    useCallback((state) => state.starredBoards, []),
  );

  const isDragAndDropEnabled = starredBoards.length > 1;

  const sortedStarredBoards = useMemo(() => {
    return starredBoards.slice().sort((a, b) => a.pos - b.pos);
  }, [starredBoards]);

  const { value: isMidpointPositioning } = useFeatureGate(
    'trello_web_positioning_midpoint',
  );

  const updateStarredBoardPosition = useCallback(
    async ({
      boardId,
      draggedOverBoardId,
      currentClosestEdge,
    }: {
      boardId: string;
      draggedOverBoardId: string;
      currentClosestEdge: Edge | null;
    }) => {
      if (!currentClosestEdge) {
        return;
      }

      const initialStarredBoards = starredBoards;

      const board = sortedStarredBoards.find(
        (starredBoard) => starredBoard.idBoard === boardId,
      );

      if (!board) {
        return;
      }

      try {
        const currentIndex = sortedStarredBoards.findIndex(
          (starredBoard) => starredBoard.idBoard === boardId,
        );
        // Find the index of the dragged over board in the full starred boards
        // list so `calculatePos` can correctly determine the new position
        const targetIndex = sortedStarredBoards.findIndex(
          (starredBoard) => starredBoard.idBoard === draggedOverBoardId,
        );

        const finishIndex = getReorderDestinationIndex({
          startIndex: currentIndex,
          indexOfTarget: targetIndex,
          closestEdgeOfTarget: currentClosestEdge,
          axis: 'horizontal',
        });

        // the starred board was dropped in the same position
        if (currentIndex === finishIndex) {
          return;
        }

        // calculatePos requires the boards to be passed in sorted order
        const newPos = isMidpointPositioning
          ? calculateItemPosition(finishIndex, sortedStarredBoards, board)
          : calculatePos(finishIndex, sortedStarredBoards, board);

        const updatedStarredBoards = sortedStarredBoards.map((starredBoard) => {
          const cloned = { ...starredBoard };
          if (cloned.idBoard === boardId) {
            cloned.pos = newPos;
          }
          return cloned;
        });

        starredBoardsState.setValue({
          starredBoards: updatedStarredBoards,
        });

        await updateStarredBoard({
          variables: {
            memberId,
            boardStarId: board.id,
            pos: newPos,
          },
        });
      } catch (err) {
        sendErrorEvent(err, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Starred Boards List',
          },
        });

        // there was an error, update board order to persistent version
        starredBoardsState.setValue({
          starredBoards: initialStarredBoards,
        });
      }
    },
    [
      isMidpointPositioning,
      memberId,
      sortedStarredBoards,
      starredBoards,
      updateStarredBoard,
    ],
  );

  useEffect(() => {
    const element = ref?.current;
    if (!element) {
      return;
    }

    return combine(
      draggable({
        element,
        canDrag: () => isDragAndDropEnabled,
        getInitialData: () => ({
          type: 'trello/starred-board',
          boardId: selectedBoardId,
        }),
        onDragStart: ({ source }) => {
          if (!isDraggableStarredBoard(source.data)) {
            return;
          }

          starredBoardDragAndDropState.setValue({
            boardId: source.data.boardId,
          });
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isDraggableStarredBoard(source.data),
        getIsSticky: () => true,
        getData: ({ input, element: el }) => {
          const data = {
            boardId: selectedBoardId,
          };
          return attachClosestEdge(data, {
            input,
            element: el,
            allowedEdges: ['left', 'right'],
          });
        },
        onDrag: ({ self }) => {
          starredBoardDragAndDropState.setValue({
            draggedOverBoardId: selectedBoardId,
            currentClosestEdge: extractClosestEdge(self.data),
          });
        },
        onDrop: ({ self, source }) => {
          if (!isDraggableStarredBoard(source.data)) {
            return;
          }

          updateStarredBoardPosition({
            boardId: source.data.boardId,
            draggedOverBoardId: selectedBoardId,
            currentClosestEdge: extractClosestEdge(self.data),
          });

          onDropStarredBoard();
        },
        onGenerateDragPreview({ nativeSetDragImage, source, location }) {
          disableNativeDragPreview({ nativeSetDragImage });
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element: source.element,
              input: location.current.input,
            }),
            render({ container }) {
              const rect = element.getBoundingClientRect();
              const preview = element.cloneNode(true) as HTMLLIElement;
              preview.style.width = `${rect.width - 8}px`;
              preview.style.opacity = '0.8';
              preview.style.rotate = '3deg';

              const boardTile = preview.firstChild?.cloneNode(
                true,
              ) as HTMLElement;
              boardTile.style.outline = 'none';

              preview.replaceChild(
                boardTile,
                preview.firstChild as HTMLElement,
              );
              container.appendChild(preview);
            },
          });
        },
      }),
    );
  });

  const dropPreviewClosestEdge = useSharedStateSelector(
    starredBoardDragAndDropState,
    useCallback(
      // We only want to show the drop preview above or below the board that's
      // currently being hovered over, and don't show the preview if hovering over
      // the original position:
      (state) => {
        const isBoardDragging = isDragging();
        if (
          selectedBoardId !== state.draggedOverBoardId ||
          state.draggedOverBoardId === state.boardId ||
          !isBoardDragging
        ) {
          return null;
        }
        return state.currentClosestEdge;
      },
      [selectedBoardId],
    ),
  );

  const dragState: 'dragging' | 'idle' | 'moving' = useSharedStateSelector(
    starredBoardDragAndDropState,
    useCallback(
      (state) => {
        const _isDragging = state.boardId === selectedBoardId;
        if (!_isDragging) {
          return 'idle';
        }

        if (
          state.draggedOverBoardId !== null &&
          state.draggedOverBoardId !== state.boardId
        ) {
          return 'moving';
        }
        return 'dragging';
      },
      [selectedBoardId],
    ),
  );

  return {
    dragState,
    dropPreviewClosestEdge,
  };
};
